import { useEffect, useMemo } from "react";

import type { ChatSource } from "@mirage/mosaics/Chat/types";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { ProgressUpdateStep } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import i18n from "@mirage/translations";

/**
 * Returns the progress string and isWaitingForResponse flag for the conversation.
 * If the conversationId is not provided, it will return undefined progressString and true when messages are loading.
 * @param setManuallyAddedSources - Optional function to reset manually added sources.
 * @param conversationId - The id of the conversation.
 * @returns The progress string and isWaitingForResponse flag.
 */
export function useConversationProgress(
  setManuallyAddedSources: (sources: ChatSource[]) => void,
  conversationId?: string,
) {
  const conversation = useConversation(conversationId);

  const isWaitingForResponse = useMemo(() => {
    const lastMessage =
      conversation?.messages?.[conversation?.messages.length - 1];
    return conversation?.messages
      ? !!conversation?.messages?.some(
          (message) => message.status === "pending",
        ) || lastMessage?.status === "streaming"
      : true; // true when loading
  }, [conversation?.messages]);

  let progressString: string | undefined;
  if (conversation?.progressUpdates?.length) {
    const lastUpdate =
      conversation?.progressUpdates[conversation?.progressUpdates.length - 1];
    progressString = getStepLabel(lastUpdate);
  } else if (isWaitingForResponse) {
    progressString = i18n.t("compose_message_waiting_for_response");
  }

  useEffect(() => {
    if (progressString) {
      setManuallyAddedSources([]);
    }
  }, [progressString, setManuallyAddedSources]);

  return { progressString, isWaitingForResponse };
}

const getStepLabel = (update: ProgressUpdateStep) => {
  switch (update) {
    case ProgressUpdateStep.STEP_SEARCH:
      return i18n.t("conversation_step_label_searching");
    case ProgressUpdateStep.STEP_CONTEXT_SELECTION:
      return i18n.t("conversation_step_label_searching");
    case ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_LLM:
      return undefined; // show nothing while text streams in
    case ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_AGENT:
      return i18n.t("conversation_step_label_generating_response_from_agent");
    case ProgressUpdateStep.STEP_SUGGESTED_QUERIES_GENERATION:
      return i18n.t("conversation_step_label_generating_suggested_queries");
    case ProgressUpdateStep.STEP_INTENT_DETECTION:
      return i18n.t("conversation_step_label_searching");
    default:
      return i18n.t("compose_message_waiting_for_response");
  }
};
